import React, { useState } from 'react';
import { PlayIcon, CheckIcon, XIcon, AlertTriangleIcon, UserIcon, DollarSignIcon, InfoIcon, TagIcon, GitBranchIcon, LinkIcon } from 'lucide-react';
import { StatusBadge } from '../../StatusBadge';
export function TestRunner({
  ruleSet,
  locale
}) {
  const [testData, setTestData] = useState({
    value: '15000',
    glCode: '5000.5200',
    vendor: 'vendor-001',
    entity: 'entity-001',
    country: 'US'
  });
  const [testResult, setTestResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDelegationReference, setShowDelegationReference] = useState(false);
  // Handle input change
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setTestData({
      ...testData,
      [name]: value
    });
  };
  // Run test
  const runTest = () => {
    setIsLoading(true);
    // Simulate API call delay
    setTimeout(() => {
      let result = {
        passed: false,
        actions: [],
        matchedConditions: [],
        delegationMatches: []
      };
      // Logic for Financial Delegation rule type
      if (ruleSet.ruleType === 'Financial Delegation') {
        const value = parseFloat(testData.value);
        // Check if document value is over delegation threshold
        if (value > 10000 && testData.glCode.startsWith('1') || testData.glCode.startsWith('2')) {
          result.passed = true;
          result.matchedConditions = ['Document.Total > User.Delegation.UpperLimit', 'UseFdoaCategories = TRUE', "Document.GLCode BETWEEN '1000' AND '2000'"];
          result.actions = [{
            type: 'delegation-route',
            description: 'Route for Delegation'
          }];
          result.delegationMatches = [{
            id: 'del-001',
            name: 'John Smith',
            glCode: '1000.1100.1110',
            entityName: 'Global Technologies Inc.',
            lowerLimit: 0,
            upperLimit: 5000
          }, {
            id: 'del-002',
            name: 'Finance Manager',
            glCode: '5000',
            entityName: 'Global Technologies Inc.',
            lowerLimit: 5000,
            upperLimit: 25000
          }];
          // Add linked rules
          result.linkedRules = [{
            name: 'VendorComplianceCheck',
            status: 'Will Execute',
            timing: 'After'
          }];
        } else {
          result.passed = false;
          result.actions = [{
            type: 'auto-approve',
            description: 'Auto-Approve'
          }];
          if (value <= 10000) {
            result.failedConditions = ['Document.Total > User.Delegation.UpperLimit'];
          }
          if (!testData.glCode.startsWith('1') && !testData.glCode.startsWith('2')) {
            result.failedConditions = [...(result.failedConditions || []), "Document.GLCode BETWEEN '1000' AND '2000'"];
          }
          // Add linked rules for failure case
          result.linkedRules = [{
            name: 'EscalationRule',
            status: 'Will Execute',
            timing: 'On Failure'
          }];
        }
      }
      // Logic for regular rules
      else {
        // Process Invoice Approval Workflow rule
        if (ruleSet.id === 'rule-001') {
          const value = parseFloat(testData.value);
          if (value > 10000 && testData.glCode === '5000.5200') {
            result.passed = true;
            result.matchedConditions = ['Value > 10000', 'GL Code = 5000.5200'];
            result.actions = [{
              type: 'route',
              description: 'Route to Finance Manager'
            }, {
              type: 'notify',
              description: 'Notify john.smith@example.com'
            }];
          } else {
            result.passed = false;
            if (value <= 10000) {
              result.failedConditions = ['Value > 10000'];
            }
            if (testData.glCode !== '5000.5200') {
              result.failedConditions = [...(result.failedConditions || []), 'GL Code = 5000.5200'];
            }
          }
        }
        // Process PO Auto-Approval rule
        else if (ruleSet.id === 'rule-002') {
          const value = parseFloat(testData.value);
          if (value < 1000 || testData.vendor === 'vendor-001' || testData.vendor === 'vendor-002') {
            result.passed = true;
            if (value < 1000) {
              result.matchedConditions = ['Value < 1000'];
            } else {
              result.matchedConditions = ['Vendor in [Office Depot, Dell Technologies]'];
            }
            result.actions = [{
              type: 'auto-approve',
              description: 'Auto-approve PO'
            }];
          } else {
            result.passed = false;
            result.failedConditions = ['Value < 1000', 'Vendor in [Office Depot, Dell Technologies]'];
          }
        }
        // Process Vendor Compliance Check rule
        else if (ruleSet.id === 'rule-003') {
          const value = parseFloat(testData.value);
          const highRiskCountries = ['RU', 'CN', 'IR'];
          if (highRiskCountries.includes(testData.country) && (testData.vendorStatus === 'New' || value > 50000)) {
            result.passed = true;
            result.matchedConditions = [`Country in [${highRiskCountries.join(', ')}]`];
            if (testData.vendorStatus === 'New') {
              result.matchedConditions.push('Vendor Status = New');
            } else {
              result.matchedConditions.push('Value > 50000');
            }
            result.actions = [{
              type: 'trigger-workflow',
              description: 'Trigger compliance-check workflow'
            }, {
              type: 'notify',
              description: 'Notify compliance-team'
            }];
          } else {
            result.passed = false;
            if (!highRiskCountries.includes(testData.country)) {
              result.failedConditions = [`Country in [${highRiskCountries.join(', ')}]`];
            }
            if (testData.vendorStatus !== 'New' && value <= 50000) {
              result.failedConditions = [...(result.failedConditions || []), 'Vendor Status = New OR Value > 50000'];
            }
          }
        }
      }
      setTestResult(result);
      setIsLoading(false);
    }, 1000);
  };
  // Render test data form
  const renderTestDataForm = () => {
    return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-9333">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-9334">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9335">
            Test Rule Set
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-9336">
            Enter test data to evaluate rule conditions
          </p>
        </div>
        <div className="px-4 py-5 sm:p-6 space-y-4" data-id="element-9337">
          <div data-id="element-9338">
            <label htmlFor="value" className="block text-sm font-medium text-gray-700" data-id="element-9339">
              Value
            </label>
            <div className="mt-1" data-id="element-9340">
              <input type="text" name="value" id="value" value={testData.value} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9341" />
            </div>
          </div>
          <div data-id="element-9342">
            <label htmlFor="glCode" className="block text-sm font-medium text-gray-700" data-id="element-9343">
              GL Code
            </label>
            <div className="mt-1" data-id="element-9344">
              <select name="glCode" id="glCode" value={testData.glCode} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9345">
                <option value="1000.1100.1110" data-id="element-9346">
                  1000.1100.1110 - Cash and Cash Equivalents
                </option>
                <option value="5000.5200" data-id="element-9347">5000.5200 - Travel Expenses</option>
                <option value="4000" data-id="element-9348">4000 - Revenue</option>
                <option value="2000.2100" data-id="element-9349">
                  2000.2100 - Current Liabilities
                </option>
              </select>
            </div>
          </div>
          <div data-id="element-9350">
            <label htmlFor="vendor" className="block text-sm font-medium text-gray-700" data-id="element-9351">
              Vendor
            </label>
            <div className="mt-1" data-id="element-9352">
              <select name="vendor" id="vendor" value={testData.vendor} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9353">
                <option value="vendor-001" data-id="element-9354">Office Depot</option>
                <option value="vendor-002" data-id="element-9355">Dell Technologies</option>
                <option value="vendor-003" data-id="element-9356">PrintWorks Inc.</option>
              </select>
            </div>
          </div>
          <div data-id="element-9357">
            <label htmlFor="entity" className="block text-sm font-medium text-gray-700" data-id="element-9358">
              Entity
            </label>
            <div className="mt-1" data-id="element-9359">
              <select name="entity" id="entity" value={testData.entity} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9360">
                <option value="entity-001" data-id="element-9361">Global Technologies Inc.</option>
                <option value="entity-002" data-id="element-9362">European Operations Ltd</option>
              </select>
            </div>
          </div>
          <div data-id="element-9363">
            <label htmlFor="country" className="block text-sm font-medium text-gray-700" data-id="element-9364">
              Country
            </label>
            <div className="mt-1" data-id="element-9365">
              <select name="country" id="country" value={testData.country} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-9366">
                <option value="US" data-id="element-9367">United States</option>
                <option value="GB" data-id="element-9368">United Kingdom</option>
                <option value="CA" data-id="element-9369">Canada</option>
                <option value="RU" data-id="element-9370">Russia</option>
                <option value="CN" data-id="element-9371">China</option>
                <option value="IR" data-id="element-9372">Iran</option>
              </select>
            </div>
          </div>
          <div className="pt-4" data-id="element-9373">
            <button type="button" onClick={runTest} disabled={isLoading} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9374">
              {isLoading ? <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-9375">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-9376"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-9377"></path>
                  </svg>
                  Testing...
                </> : <>
                  <PlayIcon className="h-4 w-4 mr-2" data-id="element-9378" />
                  Run Test
                </>}
            </button>
          </div>
        </div>
      </div>;
  };
  // Render test result
  const renderTestResult = () => {
    if (!testResult) return null;
    return <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-4" data-id="element-9379">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-9380">
          <div className="flex justify-between items-center" data-id="element-9381">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9382">
              Test Result
            </h3>
            {testResult.passed ? <StatusBadge status="success" text="Conditions Met" data-id="element-9383" /> : <StatusBadge status="error" text="Conditions Not Met" data-id="element-9384" />}
          </div>
        </div>
        <div className="px-4 py-5 sm:p-6 space-y-6" data-id="element-9385">
          {/* Matched Conditions */}
          {testResult.matchedConditions && testResult.matchedConditions.length > 0 && <div data-id="element-9386">
                <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-9387">
                  Matched Conditions
                </h4>
                <ul className="space-y-2" data-id="element-9388">
                  {testResult.matchedConditions.map((condition, index) => <li key={index} className="flex items-center text-sm text-gray-900" data-id="element-9389">
                      <CheckIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-9390" />
                      {condition}
                    </li>)}
                </ul>
              </div>}
          {/* Failed Conditions */}
          {testResult.failedConditions && testResult.failedConditions.length > 0 && <div data-id="element-9391">
                <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-9392">
                  Failed Conditions
                </h4>
                <ul className="space-y-2" data-id="element-9393">
                  {testResult.failedConditions.map((condition, index) => <li key={index} className="flex items-center text-sm text-gray-900" data-id="element-9394">
                      <XIcon className="h-4 w-4 text-red-500 mr-2" data-id="element-9395" />
                      {condition}
                    </li>)}
                </ul>
              </div>}
          {/* Delegation Matches */}
          {testResult.delegationMatches && testResult.delegationMatches.length > 0 && <div data-id="element-9396">
                <div className="flex justify-between items-center mb-2" data-id="element-9397">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-9398">
                    Delegation Matches
                  </h4>
                  <button type="button" onClick={() => setShowDelegationReference(!showDelegationReference)} className="text-xs text-indigo-600 hover:text-indigo-900" data-id="element-9399">
                    {showDelegationReference ? 'Hide Details' : 'Show Details'}
                  </button>
                </div>
                {showDelegationReference ? <div className="bg-gray-50 p-3 rounded-md border border-gray-200 mb-3" data-id="element-9400">
                    <p className="text-xs text-gray-500 mb-2" data-id="element-9401">
                      The following delegations were found that match the
                      document criteria:
                    </p>
                    <ul className="space-y-3" data-id="element-9402">
                      {testResult.delegationMatches.map((delegation, index) => <li key={index} className="text-sm" data-id="element-9403">
                          <div className="flex items-center text-gray-900 font-medium" data-id="element-9404">
                            <UserIcon className="h-4 w-4 text-gray-500 mr-1" data-id="element-9405" />
                            {delegation.name}
                          </div>
                          <div className="ml-5 grid grid-cols-2 gap-x-2 gap-y-1 text-xs text-gray-500 mt-1" data-id="element-9406">
                            <div data-id="element-9407">GL Code: {delegation.glCode}</div>
                            <div data-id="element-9408">Entity: {delegation.entityName}</div>
                            <div data-id="element-9409">
                              Limit: ${delegation.lowerLimit} - $
                              {delegation.upperLimit}
                            </div>
                          </div>
                        </li>)}
                    </ul>
                  </div> : <div className="flex items-center text-sm text-indigo-600 mb-3" data-id="element-9410">
                    <DollarSignIcon className="h-4 w-4 mr-2" data-id="element-9411" />
                    {testResult.delegationMatches.length} delegation(s) matched
                  </div>}
              </div>}
          {/* Actions */}
          <div data-id="element-9412">
            <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-9413">Actions</h4>
            <ul className="space-y-2" data-id="element-9414">
              {testResult.actions.map((action, index) => <li key={index} className="flex items-center text-sm text-gray-900 bg-gray-50 p-2 rounded" data-id="element-9415">
                  {action.type === 'route' && <UserIcon className="h-4 w-4 text-indigo-500 mr-2" data-id="element-9416" />}
                  {action.type === 'delegation-route' && <DollarSignIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-9417" />}
                  {action.type === 'notify' && <InfoIcon className="h-4 w-4 text-blue-500 mr-2" data-id="element-9418" />}
                  {action.type === 'auto-approve' && <CheckIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-9419" />}
                  {action.type === 'tag' && <TagIcon className="h-4 w-4 text-amber-500 mr-2" data-id="element-9420" />}
                  {action.type === 'trigger-workflow' && <GitBranchIcon className="h-4 w-4 text-purple-500 mr-2" data-id="element-9421" />}
                  {action.description}
                </li>)}
            </ul>
          </div>
          {/* Linked Rules */}
          {testResult.linkedRules && testResult.linkedRules.length > 0 && <div data-id="element-9422">
              <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-9423">
                Linked Rules
              </h4>
              <ul className="space-y-2" data-id="element-9424">
                {testResult.linkedRules.map((rule, index) => <li key={index} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded" data-id="element-9425">
                    <div className="flex items-center" data-id="element-9426">
                      <LinkIcon className="h-4 w-4 text-indigo-500 mr-2" data-id="element-9427" />
                      {rule.name}
                    </div>
                    <div className="flex items-center" data-id="element-9428">
                      <span className="text-xs text-gray-500 mr-2" data-id="element-9429">
                        {rule.timing}
                      </span>
                      <StatusBadge status={rule.status === 'Will Execute' ? 'info' : 'warning'} text={rule.status} data-id="element-9430" />
                    </div>
                  </li>)}
              </ul>
            </div>}
        </div>
      </div>;
  };
  return <div className="space-y-4" data-id="element-9431">
      {renderTestDataForm()}
      {renderTestResult()}
    </div>;
}